<template>
  <div class="m-2 text-left">
    <h1 class="mainheader p-2">TAIR PatMatch Results</h1>
    <p class="pl-2">
      <router-link :to="{ name: 'Patmatch' }">New pattern search</router-link>
    </p>

    <div class="sr-only" role="status" aria-live="polite">
      {{ statusMessage }}
    </div>

    <div v-if="pending" class="pl-2">
      <p>
        <span
          class="spinner-border spinner-border-sm mr-2"
          aria-hidden="true"
        ></span>
        <b>{{ job && job.status === "running" ? "Running" : "Queued" }}</b>
        <span v-if="job"> ({{ job.elapsedSeconds }} s)</span>
      </p>
      <p>
        Your PatMatch request has been queued and will be processed. Please keep
        this page open; it updates by itself.
      </p>
    </div>

    <div v-if="errorMessage" class="alert alert-danger" role="alert">
      <div style="white-space: pre-line">{{ errorMessage }}</div>
      <router-link :to="{ name: 'Patmatch' }"
        >Back to the PatMatch form</router-link
      >
    </div>

    <div v-if="summary" class="pl-2">
      <div v-if="summary.warnings.length" class="alert alert-warning">
        Job completed with the following warnings:
        <ul class="mb-0">
          <li v-for="(w, i) in summary.warnings" :key="i">{{ w }}</li>
        </ul>
      </div>

      <table class="table table-sm table-bordered summary-table">
        <caption class="sr-only">
          Search summary
        </caption>
        <tbody>
          <tr>
            <th scope="row">Hits found</th>
            <td>
              {{ summary.hitsFound.toLocaleString() }}
              <span v-if="summary.hitsFound >= summary.maxHits" class="small">
                (the maximum of {{ summary.maxHits.toLocaleString() }} hits was
                reached)
              </span>
              <div v-if="summary.hitsShown < summary.hitsFound" class="small">
                {{ summary.hitsShown.toLocaleString() }} listed below: only
                sequences with {{ summary.minSeqHits }} to
                {{ summary.maxSeqHits }} hits are shown.
              </div>
            </td>
          </tr>
          <tr>
            <th scope="row">Sequences with hits</th>
            <td>{{ summary.seqsHit.toLocaleString() }}</td>
          </tr>
          <tr>
            <th scope="row">Sequences searched</th>
            <td>{{ summary.seqsSearched.toLocaleString() }}</td>
          </tr>
          <tr>
            <th scope="row">Bytes searched</th>
            <td>{{ summary.bytesSearched.toLocaleString() }}</td>
          </tr>
          <tr>
            <th scope="row">Pattern</th>
            <td>
              <code>{{ summary.pattern }}</code>
              ({{ summary.seqClass === "pep" ? "peptide" : "nucleotide"
              }}<span v-if="summary.seqClass === 'dna'">, {{ strandText }}</span
              >)
            </td>
          </tr>
          <tr>
            <th scope="row">Mismatches allowed</th>
            <td>
              {{ summary.mismatch }}
              <span v-if="summary.mismatch > 0"
                >({{ summary.mismatchTypes.join(", ") }})</span
              >
            </td>
          </tr>
          <tr>
            <th scope="row">Dataset searched</th>
            <td>{{ summary.dataset.label }}</td>
          </tr>
          <tr v-if="summary.hitsShown">
            <th scope="row">Download all matches</th>
            <td>
              <button
                type="button"
                class="btn btn-link btn-sm p-0"
                @click="download"
              >
                Tab-separated text file
              </button>
              <span
                v-if="downloadError"
                class="text-danger small ml-2"
                role="alert"
                >{{ downloadError }}</span
              >
            </td>
          </tr>
        </tbody>
      </table>

      <template v-if="sequences.length">
        <b-pagination
          v-if="totalPages > 1"
          :value="page"
          :total-rows="totalPages"
          :per-page="1"
          align="center"
          aria-label="Result pages"
          @input="goToPage"
        />
        <table class="table table-sm table-bordered hits-table">
          <caption class="sr-only">
            Matches, grouped by sequence
          </caption>
          <thead class="thead-light">
            <tr>
              <th scope="col">Hit #</th>
              <th scope="col">Sequence name</th>
              <th scope="col"># of hits</th>
              <th scope="col">Hit pattern</th>
              <th scope="col">Start</th>
              <th scope="col">End</th>
              <th scope="col">Hit sequence</th>
            </tr>
          </thead>
          <tbody
            v-for="(seq, s) in sequences"
            :key="seq.id"
            :class="s % 2 ? 'odd-seq' : 'even-seq'"
          >
            <tr v-for="(hit, h) in seq.hits" :key="h">
              <td>{{ seq.firstHit + h }}</td>
              <template v-if="h === 0">
                <th scope="rowgroup" :rowspan="seq.hits.length">
                  <router-link
                    v-if="seq.locus"
                    :to="{ path: '/locus', query: { name: seq.locus } }"
                    >{{ seq.id }}</router-link
                  >
                  <span v-else>{{ seq.id }}</span>
                </th>
                <td :rowspan="seq.hits.length">{{ seq.hits.length }}</td>
              </template>
              <td class="match">{{ hit.match }}</td>
              <td>{{ hit.start }}</td>
              <td>{{ hit.end }}</td>
              <td>
                <button
                  type="button"
                  class="btn btn-link btn-sm p-0"
                  @click="showSequence(seq.id, hit)"
                >
                  sequence
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <b-pagination
          v-if="totalPages > 1"
          :value="page"
          :total-rows="totalPages"
          :per-page="1"
          align="center"
          aria-label="Result pages"
          @input="goToPage"
        />
      </template>
    </div>

    <b-modal
      id="patmatch-sequence"
      v-model="sequenceModal.visible"
      :title="`Sequence for ${sequenceModal.id}`"
      size="lg"
      ok-only
      ok-title="Close"
    >
      <p v-if="sequenceModal.loading">Loading…</p>
      <p v-else-if="sequenceModal.error" class="text-danger">
        {{ sequenceModal.error }}
      </p>
      <template v-else-if="sequenceModal.data">
        <p class="small mb-1">{{ sequenceModal.data.description }}</p>
        <p class="small">
          Match at {{ sequenceModal.hit.start }}-{{ sequenceModal.hit.end }}
          is highlighted.
        </p>
        <pre
          class="sequence"
        ><template v-for="(line, i) in sequenceLines"><span :key="`n${i}`" class="pos">{{ String(i * LINE_LENGTH + 1).padStart(7) }} </span><template v-for="(part, j) in line"><mark v-if="part.hit" :key="`${i}-${j}`">{{ part.text }}</mark><template v-else>{{ part.text }}</template></template>{{ "\n" }}</template></pre>
      </template>
    </b-modal>
  </div>
</template>

<script>
import PostsService from "@/services/PostsService";
import { classifyPollError, nextPollDelay } from "@/services/toolJobPolling";

export default {
  name: "PatmatchResults",
  data: () => ({
    job: null,
    summary: null,
    sequences: [],
    page: 1,
    totalPages: 1,
    errorMessage: "",
    // Separate from errorMessage, which means the job itself failed.
    downloadError: "",
    pollTimer: null,
    // Bumped by stopPolling so a request already in flight is ignored.
    pollGeneration: 0,
    pollStartedAt: Date.now(),
    // Bumped per sequence request, so only the latest one fills the modal.
    sequenceGeneration: 0,
    LINE_LENGTH: 50,
    sequenceModal: {
      visible: false,
      loading: false,
      error: "",
      id: "",
      hit: null,
      data: null,
    },
  }),
  computed: {
    jobId() {
      return this.$route.params.jobId;
    },
    pending() {
      return !this.summary && !this.errorMessage;
    },
    statusMessage() {
      if (this.summary) return "Pattern search finished.";
      if (this.errorMessage) return "Pattern search failed.";
      return "Pattern search in progress.";
    },
    strandText() {
      return {
        both: "both strands",
        watson: "Watson strand (given)",
        crick: "Crick strand (reverse complement)",
      }[this.summary.strand];
    },
    // Sequence split into lines of LINE_LENGTH, each a list of plain and
    // highlighted parts, with the chosen match highlighted.
    sequenceLines() {
      const { sequence } = this.sequenceModal.data;
      const { start, end } = this.sequenceModal.hit;
      const lo = Math.min(start, end) - 1;
      const hi = Math.max(start, end);
      const lines = [];
      for (let i = 0; i < sequence.length; i += this.LINE_LENGTH) {
        const lineEnd = Math.min(i + this.LINE_LENGTH, sequence.length);
        const cuts = [
          i,
          Math.min(Math.max(lo, i), lineEnd),
          Math.min(Math.max(hi, i), lineEnd),
          lineEnd,
        ];
        const parts = [];
        for (let c = 0; c < 3; c++) {
          if (cuts[c + 1] > cuts[c]) {
            parts.push({
              text: sequence.slice(cuts[c], cuts[c + 1]),
              hit: c === 1,
            });
          }
        }
        lines.push(parts);
      }
      return lines;
    },
  },
  watch: {
    jobId() {
      this.stopPolling();
      this.pollStartedAt = Date.now();
      this.sequenceGeneration++;
      Object.assign(this, {
        job: null,
        summary: null,
        sequences: [],
        errorMessage: "",
        downloadError: "",
      });
      Object.assign(this.sequenceModal, {
        visible: false,
        loading: false,
        error: "",
        id: "",
        hit: null,
        data: null,
      });
      this.load();
    },
    "$route.query.page"() {
      this.stopPolling();
      if (!this.errorMessage) this.load();
    },
  },
  created() {
    this.load();
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    stopPolling() {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
      this.pollGeneration++;
    },
    async load() {
      const generation = this.pollGeneration;
      let response = null;
      let failure = null;
      try {
        response = await PostsService.getPatmatchJob(
          this.jobId,
          this.$route.query.page || 1
        );
      } catch (err) {
        failure = classifyPollError(err, "PatMatch");
      }
      // The page was left or switched to another job while waiting.
      if (generation !== this.pollGeneration) return;

      if (failure) {
        if (!failure.retry) {
          this.errorMessage = failure.message;
          return;
        }
      } else {
        const { data } = response;
        this.job = data;
        if (data.status === "done") {
          this.summary = data.summary;
          this.sequences = data.sequences;
          this.page = data.page;
          this.totalPages = data.totalPages;
          return;
        }
        if (data.status === "error") {
          this.errorMessage = data.error || "The PatMatch search failed.";
          return;
        }
      }
      this.pollTimer = setTimeout(this.load, nextPollDelay(this.pollStartedAt));
    },
    goToPage(page) {
      if (page === this.page) return;
      this.$router.push({ query: { ...this.$route.query, page } });
    },
    async showSequence(id, hit) {
      const generation = ++this.sequenceGeneration;
      Object.assign(this.sequenceModal, {
        visible: true,
        loading: true,
        error: "",
        id,
        hit,
        data: null,
      });
      try {
        const { data } = await PostsService.getPatmatchSequence(this.jobId, id);
        if (generation !== this.sequenceGeneration) return;
        this.sequenceModal.data = data;
      } catch (err) {
        if (generation !== this.sequenceGeneration) return;
        this.sequenceModal.error =
          (err.response && err.response.data && err.response.data.error) ||
          "Could not load the sequence.";
      } finally {
        if (generation === this.sequenceGeneration) {
          this.sequenceModal.loading = false;
        }
      }
    },
    async download() {
      const jobId = this.jobId;
      // Ignore a download that returns after the page left this job.
      const stale = () => this._isDestroyed || jobId !== this.jobId;
      this.downloadError = "";
      try {
        const { data } = await PostsService.getPatmatchDownload(jobId);
        if (stale()) return;
        const url = URL.createObjectURL(data);
        const link = document.createElement("a");
        link.href = url;
        link.download = "patmatch_results.tsv";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (err) {
        if (stale()) return;
        this.downloadError = "Could not download the results.";
      }
    },
  },
};
</script>

<style scoped>
.mainheader {
  font-size: 16pt;
  font-weight: bold;
  color: #930;
}
.summary-table {
  width: auto;
}
.summary-table th {
  font-weight: normal;
  background: #f8f9fa;
}
.hits-table th[scope="rowgroup"] {
  font-weight: normal;
}
.hits-table .match {
  font-family: monospace;
  word-break: break-all;
  max-width: 16em;
}
.even-seq {
  background: #f5f9ff;
}
.odd-seq {
  background: #ececec;
}
.sequence {
  font-size: 12px;
}
.sequence .pos {
  color: #6c757d;
}
.sequence mark {
  background: #ffd54f;
  color: #c00;
  font-weight: bold;
  padding: 0;
}
</style>
